import type { GameMode } from '../core/types/gameMode.types';
import type {
  EvaluationBlock,
  EvaluationExam,
  ExamDefinition,
  ExamSessionState,
  StudentExamProgress,
  StudentExamSlot,
} from '../core/types/payloads/evaluacion.types';
import type { ArenaPayload, ArenaRuntime } from '../core/types/payloads/arena.types';
import type { CompletarRuntime } from '../core/types/payloads/completar.types';
import type { DesarrolloRuntime } from '../core/types/payloads/desarrollo.types';
import { validateActivityPayload, createActivityRuntime } from './pluginRunner';
import { generateJoinCode } from '../core/engine/codeGenerator';
import { demoCompletarPayload } from '../activities/completar/completar.logic';
import { demoDesarrolloPayload } from '../activities/desarrollo/desarrollo.logic';
import { demoVinculaPayload } from '../activities/vincula/vincula.logic';
import { demoAgrupaPayload } from '../activities/agrupa/agrupa.logic';
import { demoArenaPayload } from '../activities/arena/arena.logic';
import { demoSequencerPayload } from '../activities/sequencer/sequencer.logic';
import { demoPinpointPayload } from '../activities/pinpoint/pinpoint.logic';
import { demoDictadoPayload } from '../activities/dictado/dictado.logic';
import { demoAnatomiaPayload } from '../activities/anatomia/anatomia.logic';
import { demoRhythmCanvasPayload } from '../activities/rhythm_canvas/rhythm_canvas.logic';
import { demoMelodyStaffPayload } from '../activities/melody_staff/melody_staff.logic';
import { demoEarTrainingPayload } from '../activities/ear_training/ear_training.logic';
import { demoInfoBlockPayload } from '../activities/info_block/info_block.logic';
import { validateSubtituloPayload, validateDestacadoPayload, validateFiguraPayload, validateGlosarioTablaPayload, validateFormulaDestacadaPayload } from '../activities/guide_editorial/guide_editorial.logic';
import { demoCanvasBreakdownPayload } from '../activities/canvas_breakdown/canvas_breakdown.logic';
import { demoSymbolAnalysisPayload } from '../activities/symbol_analysis/symbol_analysis.logic';
import { demoGeometryCanvasPayload } from '../activities/geometry_canvas/geometry_canvas.logic';
import { demoCartesianPlanePayload } from '../activities/cartesian_plane/cartesian_plane.logic';
import { demoMathEquationPayload } from '../activities/math_equation/math_equation.logic';
import { demoNumberLinePayload } from '../activities/number_line/number_line.logic';
import { demoCountingCollectionPayload } from '../activities/counting_collection/counting_collection.logic';
import { demoDataChartPayload } from '../activities/data_chart/data_chart.logic';
import { demoFractionModelPayload } from '../activities/fraction_model/fraction_model.logic';
import { demoPercentGridPayload } from '../activities/percent_grid/percent_grid.logic';
import { demoStructuralBreakdownPayload } from '../activities/structural_breakdown/structural_breakdown.logic';
import { demoSourceAnalysisPayload } from '../activities/source_analysis/source_analysis.logic';

export function createBlockId(): string {
  return `blk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Orden pedagógico: activación → procedimiento → práctica → síntesis */
export const MODE_PEDAGOGICAL_RANK: Partial<Record<GameMode, number>> = {
  info_block: 1,
  subtitulo: 2,
  destacado: 3,
  figura: 4,
  glosario_tabla: 5,
  formula_destacada: 6,
  structural_breakdown: 15,
  source_analysis: 18,
  canvas_breakdown: 2,
  geometry_canvas: 3,
  cartesian_plane: 3,
  number_line: 3,
  counting_collection: 2,
  data_chart: 3,
  fraction_model: 3,
  percent_grid: 3,
  math_equation: 4,
  completar: 10,
  vincula: 20,
  anatomia: 25,
  rhythm_canvas: 28,
  symbol_analysis: 27,
  melody_staff: 29,
  order: 30,
  dictado: 35,
  ear_training: 36,
  agrupa: 40,
  sandbox: 45,
  arena: 55,
  desarrollo: 90,
};

export function reorderBlocksPedagogically(blocks: EvaluationBlock[]): EvaluationBlock[] {
  const sorted = [...blocks].sort((a, b) => {
    const rankA = MODE_PEDAGOGICAL_RANK[a.mode] ?? 50;
    const rankB = MODE_PEDAGOGICAL_RANK[b.mode] ?? 50;
    if (rankA !== rankB) return rankA - rankB;
    return a.index - b.index;
  });
  return sorted.map((block, index) => ({ ...block, index }));
}

export function defaultPayloadForMode(mode: GameMode): unknown {
  switch (mode) {
    case 'completar':
      return demoCompletarPayload();
    case 'desarrollo':
      return demoDesarrolloPayload();
    case 'vincula':
      return demoVinculaPayload();
    case 'agrupa':
      return demoAgrupaPayload();
    case 'arena':
      return demoArenaPayload();
    case 'order':
      return demoSequencerPayload();
    case 'point':
      return demoPinpointPayload();
    case 'dictado':
      return demoDictadoPayload();
    case 'anatomia':
      return demoAnatomiaPayload();
    case 'rhythm_canvas':
      return demoRhythmCanvasPayload();
    case 'melody_staff':
      return demoMelodyStaffPayload();
    case 'ear_training':
      return demoEarTrainingPayload();
    case 'info_block':
      return demoInfoBlockPayload();
    case 'subtitulo':
      return validateSubtituloPayload({ title: 'Nueva sección' });
    case 'destacado':
      return validateDestacadoPayload({ title: 'Sabías que…', body: 'Dato curioso sobre el tema.' });
    case 'figura':
      return validateFiguraPayload({
        altText: 'Ilustración sugerida',
        caption: 'Pie de foto explicativo.',
      });
    case 'glosario_tabla':
      return validateGlosarioTablaPayload({
        title: 'Glosario',
        entries: [{ term: 'Concepto', definition: 'Definición clave.' }],
      });
    case 'formula_destacada':
      return validateFormulaDestacadaPayload({
        title: 'Idea destacada',
        latex: 'A = B',
        description: 'Relación clave del tema (placeholder neutro).',
      });
    case 'canvas_breakdown':
      return demoCanvasBreakdownPayload();
    case 'geometry_canvas':
      return demoGeometryCanvasPayload();
    case 'cartesian_plane':
      return demoCartesianPlanePayload();
    case 'math_equation':
      return demoMathEquationPayload();
    case 'number_line':
      return demoNumberLinePayload();
    case 'counting_collection':
      return demoCountingCollectionPayload();
    case 'data_chart':
      return demoDataChartPayload();
    case 'fraction_model':
      return demoFractionModelPayload();
    case 'percent_grid':
      return demoPercentGridPayload();
    case 'symbol_analysis':
      return demoSymbolAnalysisPayload();
    case 'structural_breakdown':
      return demoStructuralBreakdownPayload();
    case 'source_analysis':
      return demoSourceAnalysisPayload();
    default:
      return { title: 'Nuevo bloque' };
  }
}

function blueprintPurposeFromBlock(block: EvaluationBlock & { _blueprintPurpose?: string }): string {
  const fromMeta = String(block._blueprintPurpose || '').trim();
  if (fromMeta) return fromMeta;
  const payload = block.payload as Record<string, unknown> | undefined;
  return String(payload?.blueprintPurpose || '').trim();
}

export function normalizeExamBlocks(exam: EvaluationExam): EvaluationBlock[] {
  return exam.blocks
    .map((block, index) => {
      const source = block as EvaluationBlock & { _blueprintPurpose?: string };
      const validated = validateActivityPayload(block.mode, block.payload);
      const blueprintPurpose = blueprintPurposeFromBlock({
        ...source,
        payload: validated as EvaluationBlock['payload'],
      });
      return {
        id: block.id || createBlockId(),
        index,
        mode: block.mode,
        label: block.label,
        payload: validated,
        ...(blueprintPurpose ? { _blueprintPurpose: blueprintPurpose } : {}),
      } as EvaluationBlock & { _blueprintPurpose?: string };
    })
    .sort((a, b) => a.index - b.index);
}

export function validateExam(exam: EvaluationExam): EvaluationExam {
  if (!exam.title?.trim()) throw new Error('La evaluación requiere título');
  if (!exam.blocks?.length) throw new Error('La evaluación requiere al menos un bloque');
  const blocks = normalizeExamBlocks(exam);
  return {
    ...exam,
    blocks,
    settings: {
      shuffleBlocks: exam.settings?.shuffleBlocks ?? false,
      allowRejoin: exam.settings?.allowRejoin ?? true,
      integrityMode: exam.settings?.integrityMode ?? 'STRICT',
      advanceMode: exam.settings?.advanceMode ?? 'AUTONOMOUS',
    },
  };
}

export function buildExamDefinition(exam: EvaluationExam): ExamDefinition {
  const blocks = normalizeExamBlocks(exam);
  return {
    examId: exam.id,
    title: exam.title,
    subjectTag: exam.subjectTag,
    blocks: blocks.map((b) => ({
      id: b.id,
      index: b.index,
      mode: b.mode,
      label: b.label,
      payload: b.payload,
    })),
  };
}

export function createExamSession(exam: EvaluationExam, blockIndex = 0): ExamSessionState {
  const blocks = normalizeExamBlocks(exam);
  return {
    examId: exam.id,
    title: exam.title,
    integrityMode: exam.settings.integrityMode,
    advanceMode: exam.settings.advanceMode,
    shuffleBlocks: exam.settings.shuffleBlocks,
    isStudyGuide: exam.isStudyGuide === true,
    currentBlockIndex: blockIndex,
    totalBlocks: blocks.length,
    status: 'ACTIVE',
    progress: {},
    blockOrders: exam.settings.shuffleBlocks ? {} : undefined,
    pendingReviews: [],
  };
}

export function getInitialStudentProgress(
  participantId: string,
  totalBlocks: number,
): StudentExamProgress {
  return {
    participantId,
    blockIndex: 0,
    completedBlocks: 0,
    totalBlocks,
    finished: false,
  };
}

export function getStudentBlockIndex(
  session: ExamSessionState,
  participantId: string,
): number {
  return session.progress[participantId]?.blockIndex ?? 0;
}

export function advanceStudentExamProgress(
  session: ExamSessionState,
  participantId: string,
): { session: ExamSessionState; finished: boolean } {
  const prev =
    session.progress[participantId] ??
    getInitialStudentProgress(participantId, session.totalBlocks);
  const nextIndex = prev.blockIndex + 1;
  const finished = nextIndex >= session.totalBlocks;
  const progress: StudentExamProgress = {
    ...prev,
    blockIndex: finished ? session.totalBlocks - 1 : nextIndex,
    completedBlocks: Math.min(prev.completedBlocks + 1, session.totalBlocks),
    finished,
  };
  return {
    session: {
      ...session,
      progress: { ...session.progress, [participantId]: progress },
    },
    finished,
  };
}

export function advanceHostExamBlock(session: ExamSessionState): ExamSessionState {
  const nextIndex = Math.min(session.currentBlockIndex + 1, session.totalBlocks - 1);
  return { ...session, currentBlockIndex: nextIndex };
}

export function getBlockAt(
  exam: EvaluationExam | ExamDefinition,
  index: number,
  blockOrder?: string[],
): EvaluationBlock | null {
  const blocks = 'blocks' in exam && Array.isArray(exam.blocks) ? exam.blocks : [];
  const byId = new Map(blocks.map((b) => [b.id, b]));

  if (blockOrder?.length) {
    const blockId = blockOrder[index];
    const block = blockId ? byId.get(blockId) : undefined;
    if (!block) return null;
    return {
      id: block.id,
      index,
      mode: block.mode as GameMode,
      label: block.label,
      payload: block.payload,
    } as EvaluationBlock;
  }

  const sorted = [...blocks].sort((a, b) => a.index - b.index);
  const block = sorted[index];
  if (!block) return null;
  return {
    id: block.id,
    index: block.index,
    mode: block.mode as GameMode,
    label: block.label,
    payload: block.payload,
  } as EvaluationBlock;
}

export function getParticipantBlockOrder(
  session: ExamSessionState,
  participantId: string,
): string[] | undefined {
  if (!session.shuffleBlocks || session.advanceMode !== 'AUTONOMOUS') return undefined;
  return session.blockOrders?.[participantId];
}

export function launchBlockRuntime(mode: GameMode, payload: unknown) {
  const validated = validateActivityPayload(mode, payload);
  const runtime = createActivityRuntime(mode, validated, 'INDIVIDUAL');
  return { mode, payload: validated, runtime };
}

export function buildStudentExamSlot(
  definition: ExamDefinition,
  session: ExamSessionState,
  participantId: string,
  runtime: unknown,
): StudentExamSlot | null {
  const progress = session.progress[participantId];
  if (progress?.finished) {
    return {
      blockIndex: session.totalBlocks,
      totalBlocks: session.totalBlocks,
      blockId: 'done',
      mode: 'arena',
      payload: { title: session.title },
      runtime: {},
      finished: true,
    };
  }
  const blockIndex = progress?.blockIndex ?? 0;
  const blockOrder = getParticipantBlockOrder(session, participantId);
  const block = getBlockAt(definition, blockIndex, blockOrder);
  if (!block) return null;
  return {
    blockIndex,
    totalBlocks: session.totalBlocks,
    blockId: block.id,
    blockLabel: block.label,
    mode: block.mode,
    payload: block.payload,
    runtime,
    finished: false,
  };
}

export function isExamBlockComplete(
  mode: GameMode,
  payload: unknown,
  runtime: unknown,
  participantId: string,
  inputAction?: string,
): boolean {
  if (inputAction === 'EXAM_CONTINUE') return true;

  switch (mode) {
    case 'info_block':
    case 'canvas_breakdown':
    case 'geometry_canvas':
      return true;
    case 'math_equation':
      return true;
    case 'completar':
    case 'vincula':
    case 'agrupa':
    case 'order': {
      if (inputAction !== 'SUBMIT') return false;
      const rt = runtime as CompletarRuntime;
      return Boolean(rt.finalized?.[participantId]);
    }
    case 'desarrollo': {
      if (inputAction !== 'SUBMIT') return false;
      const rt = runtime as DesarrolloRuntime;
      return Boolean(rt.responses?.[participantId]?.submittedAt);
    }
    case 'arena': {
      const p = payload as ArenaPayload;
      const r = runtime as ArenaRuntime;
      const answers = r.answers?.[participantId] ?? {};
      return p.questions.length > 0 && p.questions.every((q) => answers[q.id] !== undefined);
    }
    default:
      return inputAction === 'EXAM_CONTINUE';
  }
}

/** @deprecated Usar advanceStudentExamProgress o advanceHostExamBlock */
export function advanceExamSession(
  session: ExamSessionState,
  participantId: string,
): ExamSessionState {
  return advanceStudentExamProgress(session, participantId).session;
}

export function programExamAccessCode(exam: EvaluationExam): EvaluationExam {
  return { ...exam, accessCode: exam.accessCode ?? generateJoinCode() };
}

export function computeStudentProgressPct(progress: StudentExamProgress | undefined): number {
  if (!progress) return 0;
  if (progress.finished) return 100;
  return Math.round((progress.completedBlocks / Math.max(progress.totalBlocks, 1)) * 100);
}
